import React from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3,
  FileText,
  MessageSquare,
  CheckCircle,
  Zap,
  TrendingUp,
  Brain,
  Shield,
  ArrowRight,
} from 'lucide-react';
import SEO from '../components/SEO';

const solutions = [
  {
    icon: BarChart3,
    kicker: 'BUSINESS INTELLIGENCE',
    title: 'Analytics & Insights',
    description: 'Transform raw data into actionable intelligence with AI-powered analytics platforms. We build custom solutions that help organizations make data-driven decisions with confidence.',
    features: [
      'Predictive analytics & forecasting',
      'Real-time dashboards & visualization',
      'Custom KPI tracking & alerting',
      'Data pipeline automation',
    ],
    gradient: 'from-cyan-500/20 to-blue-500/20',
  },
  {
    icon: FileText,
    kicker: 'DOCUMENT PROCESSING',
    title: 'Intelligent Automation',
    description: 'Automate document-heavy workflows with intelligent extraction, classification, and processing. Reduce manual effort while improving accuracy and compliance.',
    features: [
      'OCR & intelligent data extraction',
      'Document classification & routing',
      'Automated compliance workflows',
      'Quality assurance automation',
    ],
    gradient: 'from-indigo-500/20 to-purple-500/20',
  },
  {
    icon: MessageSquare,
    kicker: 'CUSTOMER ENGAGEMENT',
    title: 'AI-Powered Interactions',
    description: 'Deploy conversational AI and personalization engines that understand your customers and deliver meaningful interactions at scale across every channel.',
    features: [
      'Conversational AI & chatbots',
      'Sentiment analysis & insights',
      'Personalization engines',
      'Multi-channel integration',
    ],
    gradient: 'from-emerald-500/20 to-cyan-500/20',
  },
];

const benefits = [
  { icon: Zap, title: 'Rapid Implementation', description: 'From proof of concept to production deployment in weeks, not months.' },
  { icon: TrendingUp, title: 'Enhanced Efficiency', description: 'Automate repetitive tasks and free your team to focus on high-value work.' },
  { icon: Brain, title: 'Intelligent Insights', description: 'Uncover patterns and opportunities hidden in your data with advanced ML.' },
  { icon: Shield, title: 'Secure & Compliant', description: 'Enterprise-grade security with regulatory compliance built in from day one.' },
];

const AISolutions = () => {
  return (
    <>
      <SEO
        title="AI Solutions"
        description="AI-powered business intelligence, document processing, conversational AI, and predictive analytics solutions built for regulated industries."
        path="/ai-solutions"
      />
    <div>
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-[120px] -top-40 -right-40" />
        <div className="absolute w-[400px] h-[400px] bg-indigo-500/10 rounded-full blur-[120px] bottom-0 -left-40" />
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-cyan-400">AI-Powered Solutions</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mt-4">
            Transform Your Business{' '}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-indigo-400">with AI</span>
          </h1>
          <p className="mt-6 text-lg text-slate-400 max-w-2xl mx-auto">
            Intelligent solutions built on decades of scientific research and real-world engineering experience, designed to solve your most complex challenges.
          </p>
        </div>
      </section>

      {/* Solutions */}
      {solutions.map((solution, index) => {
        const Icon = solution.icon;
        const reversed = index % 2 === 1;
        return (
          <section key={solution.title} className={`py-24 ${index % 2 === 1 ? 'bg-[#111827]' : ''}`}>
            <div className="max-w-7xl mx-auto px-6">
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${reversed ? 'lg:direction-rtl' : ''}`}>
                {/* Content */}
                <div className={reversed ? 'lg:order-2' : ''}>
                  <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-cyan-400">{solution.kicker}</p>
                  <h2 className="font-display text-3xl sm:text-4xl font-bold text-white tracking-tight mt-3">{solution.title}</h2>
                  <p className="mt-4 text-slate-400 text-lg leading-relaxed">{solution.description}</p>
                  <ul className="mt-8 space-y-4">
                    {solution.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Visual */}
                <div className={reversed ? 'lg:order-1' : ''}>
                  <div className={`relative h-80 rounded-2xl bg-gradient-to-br ${solution.gradient} overflow-hidden`}>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-20 h-20 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center">
                        <Icon className="w-10 h-10 text-cyan-400" />
                      </div>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-surface-0/50 to-transparent" />
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* Benefits */}
      <section className="py-24 bg-[#111827]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-cyan-400">Why Choose Us</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mt-3">
              Built for Real-World Impact
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={benefit.title}
                  className="bg-white/[0.04] backdrop-blur-xl border border-white/[0.06] rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 hover:bg-white/[0.07] hover:shadow-[0_20px_60px_rgba(14,165,233,0.12)]"
                >
                  <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-6">
                    <Icon className="w-6 h-6 text-cyan-400" />
                  </div>
                  <h3 className="font-display text-lg font-bold text-white mb-2">{benefit.title}</h3>
                  <p className="text-slate-400 text-sm">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 max-w-7xl mx-auto px-6">
        <div className="relative rounded-3xl overflow-hidden p-16 text-center bg-gradient-to-r from-cyan-600/20 via-indigo-600/20 to-cyan-600/20">
          <div className="absolute w-[300px] h-[300px] bg-cyan-500/10 rounded-full blur-[100px] -top-20 -right-20" />
          <div className="absolute w-[300px] h-[300px] bg-indigo-500/10 rounded-full blur-[100px] -bottom-20 -left-20" />
          <div className="relative">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white tracking-tight">
              Ready to Harness the Power of AI?
            </h2>
            <p className="mt-4 text-slate-300 text-lg max-w-xl mx-auto">
              Let's explore how AI can transform your operations, products, and customer experiences.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 mt-8 px-8 py-4 rounded-xl font-display font-semibold text-white text-sm transition-all duration-300 hover:-translate-y-0.5"
              style={{ background: 'linear-gradient(135deg, #0ea5e9, #06b6d4)', boxShadow: '0 0 30px rgba(14, 165, 233, 0.25)' }}
            >
              Get Started <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default AISolutions;
